import ReactDOM from 'react-dom';
import { Text } from '@lmn/design-system';
import type { Brand } from '@/domainLogic/index.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { TransportSection } from '@/app/components/hotel-detail/TransportsSection';
import { useChangeTransportModal } from '@/app/components/hotel-detail/evo/transport/EvoChangeTransportCard/ChangeTransportModalProvider';
import styled, { css } from 'styled-components';
import { useTranslation } from '@/app/components/translations/Translation';
import { useSearchSummary } from '@/app/components/hotel-detail/SearchSummary';
import { formatOccupancy } from '@/app/components/hotel-detail/package-review/PackageReviewSubheader';
import ModalHeader from '@/app/components/search/components/Modal/LayoutElements/ModalHeader';
import OpenXModal from '@/app/components/search/components/Modal/OpenXModal';
import { EVO_BODY_PADDING, EVO_BODY_PADDING_WIDE } from '@/app/components/search/components/Modal/OpenXModal/utils';
import { useIsMobile } from '@/app/components/search/utils/useSsrMedia';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';

// MPH-965: on mobile the trip-summary modal (#your-trip-modal) opens this modal via its flight
// "Change" link, so the two design-system Dialogs collide at the hardcoded paper z-index 1400 and
// which one shows is decided by DOM order — the trip summary was left visible on top and the drawer
// looked like it never opened. When stacked, raise this paper and render our own backdrop, the same
// way ChangeTransportDetailsModal stacks above this modal (MPH-474). The levels have to stay BELOW
// the 1450/1500 pair used by the details and filter modals, which in turn stack above this one:
// trip summary 1400 < our backdrop 1420 < our paper 1440 < their backdrop 1450 < their paper 1500.
const STACKED_MODAL_Z_INDEX = 1440;

const StackedBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundOverlay};
  z-index: ${STACKED_MODAL_Z_INDEX - 20};
`;

export function ChangeTransportModal() {
  const { isOpen, closeModal } = useChangeTransportModal();
  const { localization } = usePageWrapperData();
  const { commonData } = usePageWrapperData();
  const isMobile = useIsMobile(commonData.isMobileDevice);
  const { t } = useTranslation();
  const { isTrain } = useUrlSearchParams();

  const { tripDates, occupancy } = useSearchSummary({
    useRoomDates: false,
    intlDateFormat: { day: 'numeric', month: 'short' },
  });

  const { adults: adultsLabel, children: childrenLabel } = formatOccupancy(
    { adults: occupancy.adults, children: occupancy.children },
    t
  );
  const paxLabel = [adultsLabel, childrenLabel].filter(Boolean).join(', ');
  const datesLabel = tripDates ? `${tripDates.checkinFormatted} – ${tripDates.checkoutFormatted}` : '';
  const modalTitle = t(
    isTrain ? 'vacation.oss.change_transport.label_dialog.train' : 'vacation.oss.change_transport.label_dialog.flight'
  );

  const isStackedOverTripSummary =
    !!isOpen && typeof document !== 'undefined' && !!document.getElementById('your-trip-modal');

  return (
    <>
      {isStackedOverTripSummary &&
        ReactDOM.createPortal(<StackedBackdrop aria-hidden="true" onClick={closeModal} />, document.body)}
      <OpenXModal
        isOpen={isOpen || false}
        isMobile={isMobile}
        onClose={closeModal}
        fullScreen={!isMobile}
        brand={localization.brand as Brand}
        className={isMobile ? 'modal-XXL' : 'modal-XXL hotel-modal-lateral-evo'}
        disableBrowserHistoryNavigation={true}
        id="flight-change-modal"
        {...(isStackedOverTripSummary ? { style: { zIndex: STACKED_MODAL_Z_INDEX } } : {})}
        aria-label={modalTitle}
        // MPH-1050: 24px inset on desktop, 16px once the modal becomes a full-width sheet, so the
        // flight cards and the filter pills line up with the rest of the mobile page (Figma
        // 979-14073). EvoTransportPageLayout's sticky filters header cancels the same inset to
        // bleed out to the modal edges — keep the two in step through EVO_BODY_INSET*.
        // These are the standard EVO descriptors, so the body also loses its top padding (the
        // sticky header supplies the gap under the title bar) and its bottom padding becomes 16px.
        bodyPadding={EVO_BODY_PADDING_WIDE}
        bodyPaddingS={EVO_BODY_PADDING}
        bodyPaddingXS={EVO_BODY_PADDING}
        scrollShadows={localization?.configValuesApp?.evolutionEnabled}
        header={{
          sticky: true,
          element: (
            // MPH-832: the separator below the header is drawn by EvoTransportPageLayout's sticky
            // filters block. If the title bar drew its own scroll shadow too, a grey band would show
            // between the title and the filters (Figma has a single header block with one shadow).
            <ModalHeader handleClose={closeModal} disableScrollShadow={true}>
              <div
                css={css`
                  display: flex;
                  align-items: baseline;
                  gap: 6px;
                  flex-wrap: wrap;
                  justify-content: center;
                `}
              >
                <Text
                  variant="Title4"
                  css={css`
                    color: ${(p) => p.theme.kallisto.color.text.static.textPrimary};
                  `}
                >
                  {/* MPH-398 (mobile): header title is "Alternative flights"; desktop keeps "Change flight" + dates/pax */}
                  {isMobile
                    ? isTrain
                      ? t('vacation.oss.change_transport.page.title.train')
                      : t('vacation.oss.change_transport.page.title.flight')
                    : modalTitle}
                </Text>
                {!isMobile && (
                  <Text
                    variant="Body"
                    css={css`
                      color: ${(p) => p.theme.kallisto.color.text.static.textSecondary};
                    `}
                  >
                    {datesLabel ? `${datesLabel} · ${paxLabel}` : paxLabel}
                  </Text>
                )}
              </div>
            </ModalHeader>
          ),
        }}
      >
        <TransportSection
          css={`
            grid-area: flights;
          `}
        />
      </OpenXModal>
    </>
  );
}
